import React, { useState } from 'react';
import { Lock, Eye, EyeOff, GraduationCap } from 'lucide-react';

/**
 * Password gate for protected surfaces.
 * - `persistToSession: boolean` — store auth flag in sessionStorage so the
 *   user authenticates once per browser session (keyed by `gateKey`).
 * - `gateKey: string` — unique sessionStorage key (e.g. 'seascope_academy').
 * - `heading` / `bodyText` / `buttonLabel` — copy overrides for surfaces that
 *   are not the original "Access SeaScope" portal.
 */
export function PasswordGate({
  children,
  correctPassword = 'seascope2026',
  persistToSession = false,
  gateKey = 'seascope',
  heading = 'Protected Area',
  bodyText = 'Please enter the password to access this area.',
  buttonLabel = 'Access',
  Icon = Lock,
}) {
  const sessionKey = `seascope_gate_auth_${gateKey}`;
  const Glyph = Icon;
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (persistToSession) {
      try {
        return sessionStorage.getItem(sessionKey) === 'true';
      } catch {
        return false;
      }
    }
    return false;
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setError('');
      if (persistToSession) {
        try {
          sessionStorage.setItem(sessionKey, 'true');
        } catch {
          // sessionStorage unavailable — reflect state only
        }
      }
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  if (isAuthenticated) {
    return children;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-alt p-6">
      <div className="w-full max-w-md">
        <div className="rounded-card border border-line bg-white p-8 shadow-soft">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-tint">
              <Glyph className="h-8 w-8 text-teal-strong" />
            </div>
            <h1 className="mb-2 font-display text-2xl text-ink">{heading}</h1>
            <p className="text-ink-3">{bodyText}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-ink-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-btn border border-line bg-bg-alt px-4 py-3 text-ink placeholder-ink-3 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/30"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-3 hover:text-ink"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
            >
              {buttonLabel}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
